import httpService from "@/core/services/http.service.js";
import userMapper from "@/core/services/helpers/userMapper.js";
import i18nService from "../i18n.service";

function handleSignupError(context, error, fallback) {
    const messages = [];
    const data = error && error.response && error.response.data;

    if (data && data.errors && typeof data.errors === "object") {
        Object.values(data.errors).forEach((fieldErrors) => {
            if (Array.isArray(fieldErrors)) {
                messages.push(...fieldErrors);
            } else if (typeof fieldErrors === "string") {
                messages.push(fieldErrors);
            }
        });
    }

    if (messages.length === 0 && data && data.message) {
        messages.push(data.message);
    }

    if (messages.length === 0) {
        messages.push((error && error.message) || fallback);
    }

    context.commit(SET_ERROR, messages);
}

// action types
export const VERIFY_AUTH = "verifyAuth";
export const LOGIN = "login";
export const SIGNUP = "signup";
export const SIGNUP_STEP_2 = "signupStep2";
export const SIGNUP_STEP_3 = "signupStep3";
export const SIGNUP_STEP_4_ADD_STAFF = "signupStep4AddStaff";
export const SIGNUP_STEP_4_FINISH = "signupStep4Finish";
export const LOGOUT = "logout";
export const SET_COOKIE = "setCookie";
export const CLEAR_ERRORS = "clearErrors";

// mutation types
export const PURGE_AUTH = "logOut";
export const SET_AUTH = "setUser";
export const SET_ERROR = "setError";

const state = {
    errors: [],
    user: {},
    isAuthenticated: localStorage.getItem("isAuth") === "true" ? true : false,
    //isAuthenticated: true
};

const getters = {
    currentUser(state) {
        return state.user;
    },
    isAuthenticated(state) {
        return state.isAuthenticated &&
            localStorage.getItem("isAuth") === "true"
            ? true
            : false;
    },
};

const actions = {
    [SET_COOKIE]() {
        return httpService.get("/sanctum/csrf-cookie");
    },
    [LOGIN](context, credentials) {
        return new Promise((resolve, reject) => {
            context
                .dispatch(SET_COOKIE)
                .then(() => {
                    httpService
                        .post("api/b2b/login", credentials)
                        .then((response) => {
                            context.dispatch(VERIFY_AUTH);
                            resolve(response);
                        })
                        .catch((error) => {
                            context.commit(SET_ERROR, [
                                error.response.data.message,
                            ]);
                            reject(error);
                        });
                })
                .catch((error) => {
                    reject(error);
                });
        });
    },
    [SIGNUP](context, payload) {
        return new Promise((resolve, reject) => {
            context
                .dispatch(SET_COOKIE)
                .then(() => {
                    httpService
                        .post("api/b2b/signup/step/1", payload)
                        .then((response) => {
                            // Do NOT dispatch VERIFY_AUTH here, otherwise the
                            // isAuthenticated watcher in Signup.vue would redirect
                            // the user to "/" before they finish step 2 / step 3.
                            // VERIFY_AUTH will be dispatched after the LAST signup
                            // step is completed.
                            resolve(response);
                        })
                        .catch((error) => {
                            const messages = [];
                            const data = error.response && error.response.data;

                            if (
                                data &&
                                data.errors &&
                                typeof data.errors === "object"
                            ) {
                                Object.values(data.errors).forEach(
                                    (fieldErrors) => {
                                        if (Array.isArray(fieldErrors)) {
                                            messages.push(...fieldErrors);
                                        } else if (
                                            typeof fieldErrors === "string"
                                        ) {
                                            messages.push(fieldErrors);
                                        }
                                    }
                                );
                            }

                            if (messages.length === 0 && data && data.message) {
                                messages.push(data.message);
                            }

                            if (messages.length === 0) {
                                messages.push(
                                    error.message ||
                                        "Could not create account. Please try again."
                                );
                            }

                            context.commit(SET_ERROR, messages);
                            reject(error);
                        });
                })
                .catch((error) => {
                    context.commit(SET_ERROR, [
                        "Could not connect to the server. Please try again.",
                    ]);
                    reject(error);
                });
        });
    },
    [SIGNUP_STEP_2](context, payload) {
        return new Promise((resolve, reject) => {
            // payload may be a FormData (when a logo file is included) or a plain
            // object. Axios handles both transparently.
            const isFormData =
                typeof FormData !== "undefined" && payload instanceof FormData;

            const config = isFormData
                ? { headers: { "Content-Type": "multipart/form-data" } }
                : {};

            httpService
                .post("api/b2b/signup/step/2", payload, config)
                .then((response) => {
                    resolve(response);
                })
                .catch((error) => {
                    const messages = [];
                    const data = error.response && error.response.data;

                    if (
                        data &&
                        data.errors &&
                        typeof data.errors === "object"
                    ) {
                        Object.values(data.errors).forEach((fieldErrors) => {
                            if (Array.isArray(fieldErrors)) {
                                messages.push(...fieldErrors);
                            } else if (typeof fieldErrors === "string") {
                                messages.push(fieldErrors);
                            }
                        });
                    }

                    if (messages.length === 0 && data && data.message) {
                        messages.push(data.message);
                    }

                    if (messages.length === 0) {
                        messages.push(
                            error.message ||
                                "Could not save business details. Please try again."
                        );
                    }

                    context.commit(SET_ERROR, messages);
                    reject(error);
                });
        });
    },
    [SIGNUP_STEP_3](context, payload) {
        return new Promise((resolve, reject) => {
            httpService
                .post("api/b2b/signup/step/3", payload)
                .then((response) => {
                    resolve(response);
                })
                .catch((error) => {
                    handleSignupError(
                        context,
                        error,
                        "Could not save services step. Please try again."
                    );
                    reject(error);
                });
        });
    },
    [SIGNUP_STEP_4_ADD_STAFF](context, payload) {
        return new Promise((resolve, reject) => {
            httpService
                .post("api/b2b/signup/step/4-staff", payload)
                .then((response) => {
                    resolve(response);
                })
                .catch((error) => {
                    handleSignupError(
                        context,
                        error,
                        "Could not add staff member. Please try again."
                    );
                    reject(error);
                });
        });
    },
    [SIGNUP_STEP_4_FINISH](context, payload) {
        return new Promise((resolve, reject) => {
            httpService
                .post("api/b2b/signup/step/4", payload)
                .then((response) => {
                    resolve(response);
                })
                .catch((error) => {
                    handleSignupError(
                        context,
                        error,
                        "Could not finalize signup. Please try again."
                    );
                    reject(error);
                });
        });
    },
    [VERIFY_AUTH](context) {
        return httpService
            .get("/api/user")
            .then((response) => {
                context.commit(SET_AUTH, response.data.data);
            })
            .catch(() => {
                context.commit(PURGE_AUTH);
            });
    },
    [LOGOUT](context) {
        return new Promise((resolve, reject) => {
            httpService
                .post("/logout")
                .then((response) => {
                    context.commit(PURGE_AUTH);
                    resolve(response);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    },
    [CLEAR_ERRORS](context) {
        context.commit(SET_ERROR, {});
    },
};

const mutations = {
    [SET_ERROR](state, error) {
        state.errors = error;
    },
    [SET_AUTH](state, user) {
        // Store authenticated user
        state.isAuthenticated = true;
        localStorage.setItem("isAuth", true);
        state.user = userMapper.mapUser(user);
        state.errors = {};

        // Set language based on user industry
        i18nService.loadLanguageAsync(
            i18nService.getActiveLanguage(),
            state.user.companyIndustry
        );
    },
    [PURGE_AUTH](state) {
        localStorage.setItem("isAuth", false);
        state.isAuthenticated = false;
        state.user = {};
        state.errors = {};
    },
};

export default {
    state,
    actions,
    mutations,
    getters,
};
